/**
 * Zustand store for Celery sync job status polling.
 *
 * Polls /api/sync/status/ at 3s intervals, derives overall status from jobs,
 * and auto-stops after 2 consecutive "done" polls (avoiding infinite polling).
 */

import { create } from 'zustand';
import { type SyncJob, SyncStatusResponseSchema } from '@/api/schemas/sync';
import { getLogger } from '@/lib/logging';

const logger = getLogger('opus.sync-status');

type SyncSummary = 'idle' | 'syncing' | 'error' | 'done';

interface SyncStatusState {
  jobs: SyncJob[];
  summary: SyncSummary;
  lastUpdated: string | null;
  isPolling: boolean;
  /** True while the status bar should be visible (including 4 s linger after done). */
  barVisible: boolean;

  // Actions
  startPolling(): void;
  stopPolling(): void;
  _fetchStatus(): Promise<void>;
}

let pollingInterval: ReturnType<typeof setInterval> | null = null;
let dismissTimer: ReturnType<typeof setTimeout> | null = null;
let consecutiveDoneCount = 0;
let consecutiveErrorCount = 0;

export const useSyncStatus = create<SyncStatusState>((set, get) => ({
  jobs: [],
  summary: 'idle',
  lastUpdated: null,
  isPolling: false,
  barVisible: false,

  async _fetchStatus() {
    try {
      const response = await fetch('/api/sync/status/', {
        credentials: 'include',
      });
      if (!response.ok) {
        logger.warn({ status: response.status }, 'sync_status_fetch_failed');
        consecutiveErrorCount += 1;
        if (consecutiveErrorCount >= 3) get().stopPolling();
        return;
      }

      consecutiveErrorCount = 0;
      const data = await response.json();
      const parsed = SyncStatusResponseSchema.parse(data);

      // Derive summary from jobs
      const statuses = new Set(parsed.jobs.map((j) => j.status));
      let newSummary: SyncSummary = 'idle';

      if (statuses.has('running')) {
        newSummary = 'syncing';
        consecutiveDoneCount = 0;
      } else if (statuses.has('failed')) {
        newSummary = 'error';
        consecutiveDoneCount = 0;
      } else if (
        parsed.jobs.length > 0 &&
        parsed.jobs.every((j) => j.status === 'done' || j.status === 'skipped')
      ) {
        newSummary = 'done';
        consecutiveDoneCount += 1;
      } else {
        newSummary = 'idle';
        consecutiveDoneCount = 0;
      }

      // Only show the bar when there is actual work to report.
      // barVisible stays false on idle — it gets set true here and cleared
      // by the dismiss timer in stopPolling().
      const shouldBeVisible = newSummary !== 'idle';

      set({
        jobs: parsed.jobs,
        summary: newSummary,
        lastUpdated: parsed.lastUpdated,
        ...(shouldBeVisible ? { barVisible: true } : {}),
      });

      // Auto-stop after 2 consecutive "done" polls (6 seconds at 3s interval)
      if (consecutiveDoneCount >= 2) {
        get().stopPolling();
      }
    } catch (err) {
      logger.error({ error: String(err) }, 'sync_status_error');
      consecutiveErrorCount += 1;
      if (consecutiveErrorCount >= 3) get().stopPolling();
    }
  },

  startPolling() {
    const state = get();
    if (state.isPolling) {
      return; // Already polling
    }

    // Cancel any pending dismiss so a new sync immediately shows the bar.
    if (dismissTimer) { clearTimeout(dismissTimer); dismissTimer = null; }

    set({ isPolling: true });
    consecutiveDoneCount = 0;
    consecutiveErrorCount = 0;

    // Fetch immediately, then every 3s
    state._fetchStatus();
    pollingInterval = setInterval(() => {
      get()._fetchStatus();
    }, 3000);
  },

  stopPolling() {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
    set({ isPolling: false });
    consecutiveDoneCount = 0;

    // Hide the bar 4 s after polling stops (gives the user time to see "done").
    if (dismissTimer) clearTimeout(dismissTimer);
    dismissTimer = setTimeout(() => {
      dismissTimer = null;
      set({ barVisible: false });
    }, 4000);
  },
}));
